import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import type { FileUploadProps } from '@gov-nx/core/hooks';
import {
	useContactDetailsFormDefinition,
	useFileUpload,
} from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import type { WebFileProps } from '@gov-nx/ui/types';
import {
	phoneShapeToString,
	useAutocompleteDefinition,
} from '@gov-nx/utils/common';
import type { PageCode } from '../definitions/codes';
import type {
	EPeticeDataRequest,
	EPeticeFormDataFOStep2,
	EPeticeFormDataPOStep2,
	EPeticeFormDataStep1,
} from './wizzard.types';

export interface FormInstanceProps {
	code: PageCode;
}

export function FormInstanceStep1({
	code,
}: FormInstanceProps): FormDefinition<EPeticeFormDataStep1> {
	const { t } = useLocale(code);

	const formSchema = yup
		.object({
			nazev: yup
				.string()
				.max(200, t('formular.validace.nazev.max'))
				.required(t('formular.validace.nazev.povinny')),
			zakladatelTyp: yup.string<'PO' | 'FO'>().oneOf(['PO', 'FO']),
		})
		.required();

	const formMethods = useGovForm<EPeticeFormDataStep1>({
		formSchema,
		defaultValues: {
			nazev: undefined,
			zakladatelTyp: undefined,
		},
	});

	return getFormDefinition({ formMethods, formSchema });
}

const useCommonFormDefinition = (code: PageCode) => {
	const { t } = useLocale(code);

	const adresatOvm = useAutocompleteDefinition({
		name: 'adresatOvm',
		placeholder: t('formular.placeholder.adresat'),
		label: t('formular.pole.adresat'),
		requiredMessage: t('formular.validace.adresat.povinny'),
	});

	return {
		schema: {
			perex: yup
				.string()
				.max(290, t('formular.validace.perex.max'))
				.required(t('formular.validace.perex.povinny')),
			text: yup
				.string()
				.max(3500, t('formular.validace.text.max'))
				.required(t('formular.validace.text.povinny')),
			adresatOvm: adresatOvm.schema,
			priloha: yup.array(),
		},
		defaultValues: {
			perex: '',
			text: '',
			adresatOvm: adresatOvm.defaultValue,
			priloha: undefined,
		},
	};
};

interface Step2Props extends FormInstanceProps {
	petitionAttachmentsOptions: FileUploadProps;
}

export interface FormStep2PO {
	fields: {
		attachments: WebFileProps;
	};
	formDefinition: FormDefinition<EPeticeFormDataPOStep2>;
}

export function FormInstancePOStep2({
	code,
	petitionAttachmentsOptions,
}: Step2Props): FormStep2PO {
	const { schema, defaultValues } = useCommonFormDefinition(code);
	const contactDetailsFormDefinition = useContactDetailsFormDefinition();
	const formSchema = yup
		.object({
			...schema,
			...contactDetailsFormDefinition.shape,
			zakladatelPoIco: yup.number().required(),
		})
		.required();

	const formMethods = useGovForm<EPeticeFormDataPOStep2>({
		formSchema,
		defaultValues: {
			...defaultValues,
			...contactDetailsFormDefinition.defaultValues,
			zakladatelPoIco: undefined,
		},
	});

	const fileUploadHook = useFileUpload(
		formMethods,
		'priloha',
		petitionAttachmentsOptions
	);

	return {
		fields: {
			attachments: {
				field: { name: 'priloha', expanded: true },
				// label: { children: t('formular.pole.priloha') },
				fileUploadHook,
			},
		},
		formDefinition: getFormDefinition({ formMethods, formSchema }),
	};
}

export interface FormStep2FO {
	fields: {
		attachments: WebFileProps;
	};
	formDefinition: FormDefinition<EPeticeFormDataFOStep2>;
}

export function FormInstanceFOStep2({
	code,
	petitionAttachmentsOptions,
}: Step2Props): FormStep2FO {
	const { schema, defaultValues } = useCommonFormDefinition(code);
	const contactDetailsFormDefinition = useContactDetailsFormDefinition();

	const formSchema = yup
		.object({
			...schema,
			...contactDetailsFormDefinition.shape,
			_useAddress: yup.boolean(),
			adresa: yup.object(),
			clenovePeticnihoVyboru: yup.array().of(
				yup.object({
					jmeno: yup.string(),
					prijmeni: yup.string(),
					nazevObce: yup.string(),
					nazevUlice: yup.string(),
					cislo: yup.string(),
				})
			),
		})
		.required();

	const formMethods = useGovForm<EPeticeFormDataFOStep2>({
		formSchema,
		defaultValues: {
			...defaultValues,
			...contactDetailsFormDefinition.defaultValues,
			_useAddress: false,
			adresa: undefined,
			clenovePeticnihoVyboru: [],
		},
	});

	const fileUploadHook = useFileUpload(
		formMethods,
		'priloha',
		petitionAttachmentsOptions
	);

	return {
		fields: {
			attachments: {
				field: { name: 'priloha', expanded: true },
				fileUploadHook,
			},
		},
		formDefinition: getFormDefinition({ formMethods, formSchema }),
	};
}

export const prepareSubmitData = (
	step1FormData: EPeticeFormDataStep1,
	step2FormData?: EPeticeFormDataPOStep2 | EPeticeFormDataFOStep2
): EPeticeDataRequest => {
	if (step1FormData.zakladatelTyp === 'PO') {
		const data = step2FormData as EPeticeFormDataPOStep2;
		return {
			zakladatelTyp: 'PO',
			nazev: step1FormData.nazev,
			perex: data.perex,
			text: data.text,
			adresatOvmId: data.adresatOvm?.selected?.id ?? 0,
			zakladatelPoIco: data.zakladatelPoIco,
			kontaktniUdaje: {
				adresa: {
					kod: '',
					adresaText: '',
				},
				email: data._useEmail ? data.email : undefined,
				telefon:
					data._usePhone && data.phone
						? phoneShapeToString(data.phone)
						: undefined,
			},
		};
	}

	const dataFO = step2FormData as EPeticeFormDataFOStep2;

	return {
		zakladatelTyp: 'FO',
		nazev: step1FormData.nazev,
		perex: dataFO.perex,
		text: dataFO.text,
		adresatOvmId: dataFO.adresatOvm?.selected?.id ?? 0,
		clenovePeticnihoVyboru: dataFO.clenovePeticnihoVyboru ?? [],
		kontaktniUdaje: {
			adresa: {
				kod: dataFO._useAddress && dataFO.adresa?.kod ? dataFO.adresa.kod : '',
				adresaText:
					dataFO._useAddress && dataFO.adresa?.adresaText
						? dataFO.adresa.adresaText
						: '',
			},
			email: dataFO._useEmail ? dataFO.email : '',
			telefon:
				dataFO._usePhone && dataFO.phone
					? phoneShapeToString(dataFO.phone)
					: '',
		},
	};
};
